import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { Loader2, Receipt } from "lucide-react";

interface ServiceRequest {
  id: string;
  service_type: string;
  business_name: string;
  contact_name: string;
  email: string;
  phone: string | null;
  description: string;
  budget_range: string | null;
}

type Currency = "MAD" | "USD" | "EUR" | "GBP";

const CURRENCIES = [
  { value: "MAD", label: "MAD (د.م.)" },
  { value: "USD", label: "USD ($)" },
  { value: "EUR", label: "EUR (€)" },
  { value: "GBP", label: "GBP (£)" },
];

interface CompleteWithPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceRequest: ServiceRequest | null;
  onSuccess: () => void;
}

export function CompleteWithPaymentDialog({
  open,
  onOpenChange,
  serviceRequest,
  onSuccess,
}: CompleteWithPaymentDialogProps) {
  const [loading, setLoading] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [currency, setCurrency] = useState<Currency>("MAD");
  const [transactionId, setTransactionId] = useState("");

  const handleComplete = async () => {
    if (!serviceRequest || !paymentAmount) return;

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Update request status
      const { error: statusError } = await supabase
        .from("service_requests")
        .update({ status: "completed" })
        .eq("id", serviceRequest.id);

      if (statusError) throw statusError;

      // Check if project already exists for this service request
      const { data: existingProjects } = await supabase
        .from("projects")
        .select("id")
        .eq("service_request_id", serviceRequest.id)
        .limit(1);

      let projectId: string;
      
      if (existingProjects && existingProjects.length > 0) {
        // Update existing project to completed status
        projectId = existingProjects[0].id;
        const { error: updateError } = await supabase
          .from("projects")
          .update({ status: "completed" })
          .eq("id", projectId);
        
        if (updateError) throw updateError;
      } else {
        // Create new project only if doesn't exist
        const { data: newProject, error: projectError } = await supabase
          .from("projects")
          .insert({
            name: `${serviceRequest.service_type} - ${serviceRequest.business_name}`,
            description: serviceRequest.description,
            status: "completed",
            service_request_id: serviceRequest.id,
            user_id: user.id,
          })
          .select()
          .single();

        if (projectError) throw projectError;
        projectId = newProject.id;
      }

      // Record payment linked to project
      await supabase.from("subscriptions").insert({
        plan_name: `${serviceRequest.service_type} - ${serviceRequest.business_name}`,
        price: parseFloat(paymentAmount),
        currency: currency,
        payment_status: "completed",
        payment_type: "one-time",
        project_id: projectId,
        transaction_id: transactionId || `SR-${serviceRequest.id.slice(0, 8)}`,
        client_name: serviceRequest.contact_name,
        client_email: serviceRequest.email,
        client_phone: serviceRequest.phone,
        user_id: user.id,
      });

      toast({
        title: "Service Completed",
        description: "Request marked as completed and payment recorded!",
      });

      setPaymentAmount("");
      setTransactionId("");
      onOpenChange(false);
      onSuccess();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Complete Service & Record Payment</DialogTitle>
          <DialogDescription>
            Enter the final payment amount for this completed service
          </DialogDescription>
        </DialogHeader>

        {serviceRequest && (
          <div className="space-y-4">
            <div className="p-4 rounded-lg bg-muted/50 space-y-2">
              <p className="text-sm font-medium">{serviceRequest.business_name}</p>
              <p className="text-sm text-muted-foreground">{serviceRequest.service_type}</p>
              {serviceRequest.budget_range && (
                <p className="text-sm text-muted-foreground">Budget: {serviceRequest.budget_range}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="currency">Currency</Label>
                <Select value={currency} onValueChange={(val) => setCurrency(val as Currency)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((curr) => (
                      <SelectItem key={curr.value} value={curr.value}>
                        {curr.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount">Final Amount *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="1000.00"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="transactionId">Transaction/Reference ID</Label>
              <Input
                id="transactionId"
                value={transactionId}
                onChange={(e) => setTransactionId(e.target.value)}
                placeholder="TXN-12345 or leave blank for auto-generated"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleComplete}
                className="flex-1"
                disabled={loading || !paymentAmount}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Completing...
                  </>
                ) : (
                  <>
                    <Receipt className="mr-2 h-4 w-4" />
                    Complete & Record
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
